import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { dateDiffYears, dateToApi, dateToMMMMYYYY, dateToYYYYMM, dateUtcToS, dateUtcToyyyyMMdd } from '../../util/dateUtil';
import { logException } from '../../services/appInsights';
import { makeStyles } from '@material-ui/core/styles';
import {
  FormControlLabel,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Card from '../Card/Card.js';
import CardBody from '../Card/CardBody.js';
import TableStyle from '../../styles/TableStyle';
import TitleBar from '../Shared/TitleBar';
import { inject, observer } from 'mobx-react';
import { btOrangeColor } from '../../assets/jss/nextjs-material-kit-pro';
import PleaseWait from '../Shared/PleaseWait';
import FamilyHomeListCards from './FamilyHomeListCards';
import { callApi } from '../../util/apiWrapper';
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation';
import WarningIcon from '@material-ui/icons/Warning';
import Paper from '@material-ui/core/Paper';
import PATH from '../../constants/paths';
import { CONTENT_TYPE, METHOD } from '../../constants/method';
import { standarizeEmail, isStudent, isAlumni, isFHT } from '../../util/miscUtil';
import { useSeniorListTableStyles } from './SeniorListTable.styles';
import { fetchSeniors, makeInnerSort, createUniqueList } from './seniorListFunctions';
import PageUnauthorized from '../Shared/PageUnauthorized';

const tableStyles = makeStyles(TableStyle);

const hasNonEpochDate = (value) => {
  if (value == null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (value instanceof Date) return !Number.isNaN(value.getTime()) && value.getTime() > 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (trimmed === '1970-01-01T00:00:00Z') return false;
    if (trimmed.includes('Date(0)')) return false;
    return true;
  }
  return true;
};

const parseApiDateMs = (value) => {
  if (value == null) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/Date\((\d+)/);
  if (match) {
    const ms = Number(match[1]);
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  }
  const parsed = Date.parse(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const countCoursesInModule = (mod) => {
  const steps = mod?.steps;
  if (!Array.isArray(steps) || steps.length === 0) return 0;
  let count = 0;
  for (const step of steps) {
    if (Array.isArray(step?.courses)) count += step.courses.length;
  }
  return count;
};

const getSeniorPlanIdFromModule = (mod) => {
  const candidate = mod?.senior_plan_id ?? mod?.seniorPlanId ?? mod?.seniorPlanID;
  const parsed = Number(candidate);
  return Number.isFinite(parsed) ? parsed : null;
};

export const chooseBetterModuleForNumber = (candidate, current) => {
  if (!current) return candidate;
  if (!candidate) return current;

  const candPlanId = getSeniorPlanIdFromModule(candidate);
  const currPlanId = getSeniorPlanIdFromModule(current);
  if (candPlanId != null || currPlanId != null) {
    if (candPlanId != null && currPlanId == null) return candidate;
    if (candPlanId == null && currPlanId != null) return current;
    if (candPlanId != null && currPlanId != null && candPlanId !== currPlanId) return candPlanId > currPlanId ? candidate : current;
  }

  const candCreated = parseApiDateMs(candidate?.created_at ?? candidate?.createdAt);
  const currCreated = parseApiDateMs(current?.created_at ?? current?.createdAt);
  if (candCreated != null || currCreated != null) {
    if (candCreated != null && currCreated == null) return candidate;
    if (candCreated == null && currCreated != null) return current;
    if (candCreated != null && currCreated != null && candCreated !== currCreated) return candCreated > currCreated ? candidate : current;
  }

  const candCourses = countCoursesInModule(candidate);
  const currCourses = countCoursesInModule(current);
  if (candCourses !== currCourses) return candCourses > currCourses ? candidate : current;

  const candHasAnyCompleted = (candidate?.steps ?? []).some((step) => (step?.courses ?? []).some((c) => hasNonEpochDate(c?.completed)));
  const currHasAnyCompleted = (current?.steps ?? []).some((step) => (step?.courses ?? []).some((c) => hasNonEpochDate(c?.completed)));
  if (candHasAnyCompleted !== currHasAnyCompleted) return candHasAnyCompleted ? candidate : current;

  const candHasAnyRegistered = (candidate?.steps ?? []).some((step) => (step?.courses ?? []).some((c) => hasNonEpochDate(c?.registered)));
  const currHasAnyRegistered = (current?.steps ?? []).some((step) => (step?.courses ?? []).some((c) => hasNonEpochDate(c?.registered)));
  if (candHasAnyRegistered !== currHasAnyRegistered) return candHasAnyRegistered ? candidate : current;

  return current;
};

export const normalizeWorkbookModulesForDisplay = (modules, maxModuleNumber = 6) => {
  if (!Array.isArray(modules) || modules.length === 0) return [];

  const modulesByNumber = Array.from({ length: maxModuleNumber });
  for (let index = 0; index < modules.length; index++) {
    const mod = modules[index];
    const candidate = mod?.module_number ?? mod?.moduleNumber ?? mod?.step_number ?? mod?.stepNumber ?? null;
    const parsedNumber = Number(candidate);
    const moduleNumber = Number.isFinite(parsedNumber) ? parsedNumber : null;
    if (moduleNumber == null || moduleNumber < 1 || moduleNumber > maxModuleNumber) continue;

    modulesByNumber[moduleNumber - 1] = chooseBetterModuleForNumber(mod, modulesByNumber[moduleNumber - 1]);
  }

  return modulesByNumber.filter(Boolean);
};

const FamilyHomeListTable = inject(
  'SeniorStore',
  'AuthStore'
)(
  observer((props) => {
    const store = props.SeniorStore;
    const auth = props.AuthStore;

    const history = useHistory();

    const tableClasses = tableStyles();
    const classes = useSeniorListTableStyles();

    const [sortBy, setSortBy] = React.useState('');
    const [sortDirection, setSortDirection] = React.useState('asc');
    const [filter, setFilter] = React.useState('');
    const [allSeniorsRaw, setAllSeniorsRaw] = React.useState([]);
    const [seniors, setSeniors] = React.useState([]);

    const [pageTitle, setPageTitle] = useState('Student Workbooks');

    const [workbookModulesByUserId, setWorkbookModulesByUserId] = useState({});
    const [workbookModulesLoadingByUserId, setWorkbookModulesLoadingByUserId] = useState({});

    const isMountedRef = React.useRef(false);
    const workbookModulesByUserIdRef = React.useRef(workbookModulesByUserId);
    const workbookModulesLoadingByUserIdRef = React.useRef(workbookModulesLoadingByUserId);
    const workbookModulesRequestSeqRef = React.useRef(0);
    const workbookModulesInFlightTokenByUserIdRef = React.useRef({});

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      workbookModulesByUserIdRef.current = workbookModulesByUserId;
    }, [workbookModulesByUserId]);

    useEffect(() => {
      workbookModulesLoadingByUserIdRef.current = workbookModulesLoadingByUserId;
    }, [workbookModulesLoadingByUserId]);

    const EPOCH_DATE = /Date(0)/;

    const getSeniorUserId = (senior) => senior?.userId ?? senior?.user?.id ?? null;

    const setSort = (val) => {
      if (val === sortBy) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(val);
        setSortDirection('asc');
      }
    };

    const filterData = (val) => setFilter(val);

    const handleClickId = (event, id) => {
      event.preventDefault();
      event.stopPropagation();
      store.setBasicInfoId(id);
      store.setAlumni(false);
      history.push('/seniorplanner');
    };

    // Re-run effects when AuthStore hydrates
    const authReadyKey = [
      auth?.token,
      auth?.accessToken,
      auth?.jwt,
      auth?.idToken,
      auth?.user?.id,
      Array.isArray(auth?.roles) ? auth.roles.join('|') : '',
    ]
      .filter(Boolean)
      .join('|');

    const staffSitesKey = (store.staffsites ?? []).join('|');
    const staffStudentsKey = (store.staffstudents ?? []).join('|');

    useEffect(() => {
      const run = async () => {
        const apiSeniors = await fetchSeniors({ auth, store, roleId: 1 });
        setAllSeniorsRaw(apiSeniors);
        setSortBy('last_name');
      };

      if (auth && authReadyKey) run();
    }, [auth, authReadyKey, store, staffSitesKey, staffStudentsKey]);

    // Rerun filtering when advanced filter values change
    useEffect(() => {
      let filteredData = Array.isArray(allSeniorsRaw) ? allSeniorsRaw : [];

      setPageTitle('Student Workbooks');

      if (filter && filter.length > 0) {
        const filterNoSpaces = (filter || '').replace(/\s+/g, '');
        const filterLower = filterNoSpaces.toLowerCase();

        filteredData = filteredData.filter(
          (s) =>
            (s.int_id != null && s.int_id.toString().includes(filterLower)) ||
            (s.first_name && s.first_name.toLowerCase().includes(filterLower)) ||
            (s.last_name && s.last_name.toLowerCase().includes(filterLower)) ||
            (s.dob && dateUtcToS(s.dob) && dateUtcToS(s.dob).toString().includes(filterLower)) ||
            (s.graduation_date &&
              s.graduation_date > EPOCH_DATE &&
              dateToMMMMYYYY(s.graduation_date) &&
              dateToMMMMYYYY(s.graduation_date).toString().toLowerCase().includes(filterLower)) ||
            (s.graduation_date &&
              s.graduation_date > EPOCH_DATE &&
              dateToApi(s.dob) &&
              dateToApi(s.graduation_date) &&
              dateDiffYears(dateToApi(s.dob), dateToApi(s.graduation_date)) != null &&
              dateDiffYears(dateToApi(s.dob), dateToApi(s.graduation_date)).toString().includes(filterLower)) ||
            (s.site && s.site.toLowerCase().includes(filterLower)) ||
            (s.user &&
              s.user.email &&
              standarizeEmail(s.user.email) &&
              standarizeEmail(s.user.email).toString().toLowerCase().includes(filterLower)) ||
            (s.program && s.program.toLowerCase().includes(filterLower))
        );
      }

      store.setPage(0);

      if (!store.showAllRecords) {
        filteredData = filteredData.filter((x) => x.departure_date === undefined);
      }
      if (store.showNeedEmail) {
        filteredData = filteredData.filter((x) => (x.user?.id ?? 0) === 0);
      }

      store.setTotalSeniors?.(filteredData.length);

      filteredData = filteredData.sort(
        makeInnerSort({
          key: sortBy,
          order: sortDirection,
          standarizeEmail,
          dateToYYYYMM,
          dateUtcToyyyyMMdd,
        })
      );

      setSeniors(filteredData);
      store.setAllSeniors(filteredData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store, sortBy, sortDirection, filter, allSeniorsRaw, staffSitesKey, staffStudentsKey, store.showNeedEmail, store.showAllRecords]);

    const fetchWorkbookModulesForStudent = React.useCallback(
      async (userId) => {
        if (!userId) return [];
        try {
          const modulelist = [];
          const workbookModules = await callApi(auth, PATH.SENIOR_PLAN_DETAILS(userId), METHOD.GET, null, CONTENT_TYPE.APPLICATION_JSON);
          const results = Array.isArray(workbookModules?.result) ? workbookModules.result : [];
          results.forEach((w) => {
            const mods = Array.isArray(w?.modules) ? w.modules : [];
            mods.forEach((m) => modulelist.push(m));
          });
          return modulelist;
        } catch (err) {
          logException(err);
          console.error(err);
          return [];
        }
      },
      [auth]
    );

    useEffect(() => {
      let cancelled = false;

      const fetchModulesForVisibleRows = async () => {
        const uniqueSeniors = createUniqueList(seniors).filter(Boolean);

        const loadedByUserId = workbookModulesByUserIdRef.current;
        const loadingByUserId = workbookModulesLoadingByUserIdRef.current;
        const inFlightByUserId = workbookModulesInFlightTokenByUserIdRef.current;

        const userIdsToFetch = uniqueSeniors
          .map((senior) => getSeniorUserId(senior))
          .filter(Boolean)
          .filter((userId) => !Object.prototype.hasOwnProperty.call(loadedByUserId, userId) && !loadingByUserId[userId] && !inFlightByUserId[userId]);

        if (userIdsToFetch.length === 0) return;

        const concurrency = 5;
        const queue = [...userIdsToFetch];

        const worker = async () => {
          while (!cancelled && queue.length > 0) {
            const userId = queue.shift();
            if (!userId) continue;

            const requestToken = (workbookModulesRequestSeqRef.current += 1);
            workbookModulesInFlightTokenByUserIdRef.current[userId] = requestToken;

            setWorkbookModulesLoadingByUserId((prev) => ({ ...prev, [userId]: true }));
            const modules = await fetchWorkbookModulesForStudent(userId);

            if (!isMountedRef.current) return;
            if (workbookModulesInFlightTokenByUserIdRef.current[userId] !== requestToken) continue;

            setWorkbookModulesByUserId((prev) => ({ ...prev, [userId]: modules }));
            setWorkbookModulesLoadingByUserId((prev) => ({ ...prev, [userId]: false }));

            const { [userId]: _ignored, ...rest } = workbookModulesInFlightTokenByUserIdRef.current;
            workbookModulesInFlightTokenByUserIdRef.current = rest;
          }
        };

        await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, () => worker()));
      };

      fetchModulesForVisibleRows();
      return () => {
        cancelled = true;
      };
    }, [seniors, store.page, store.perPage, fetchWorkbookModulesForStudent]);

    const getWorkbookCompletionStatus = (mod) => {
      const steps = mod?.steps ?? [];
      if (!mod || steps.length === 0) return 'N/A';
      const courses = steps.flatMap((step) => step?.courses ?? []);

      if (courses.length === 0) return '0%';

      const allCompleted = courses.every((course) => hasNonEpochDate(course?.completed));
      if (allCompleted) return '100%';

      const anyRegistered = courses.some((course) => hasNonEpochDate(course?.registered));
      if (anyRegistered) return 'In Progress';

      return '0%';
    };

    const renderWorkbookModuleCells = (senior) => {
      const userId = getSeniorUserId(senior);
      const rawModules = (userId && workbookModulesByUserId[userId]) || [];
      const hasLoaded = userId ? Object.prototype.hasOwnProperty.call(workbookModulesByUserId, userId) : false;
      const isLoading = userId ? !!workbookModulesLoadingByUserId[userId] : false;
      const maxCells = 6;

      const modulesByNumber = Array.from({ length: maxCells });
      if (Array.isArray(rawModules) && rawModules.length > 0) {
        for (let index = 0; index < rawModules.length; index++) {
          const mod = rawModules[index];
          const candidate = mod?.module_number ?? mod?.moduleNumber ?? null;
          const moduleNumber = Number(candidate);
          if (!Number.isFinite(moduleNumber)) continue;
          if (moduleNumber < 1 || moduleNumber > maxCells) continue;
          modulesByNumber[moduleNumber - 1] = chooseBetterModuleForNumber(mod, modulesByNumber[moduleNumber - 1]);
        }
      }

      return Array.from({ length: maxCells }, (_, index) => {
        const mod = modulesByNumber[index];
        return (
          <TableCell key={`${senior.id}-module-${index}`} style={{ width: '110px', textAlign: 'center' }}>
            {!userId ? 'N/A' : !hasLoaded || isLoading ? 'Loading' : getWorkbookCompletionStatus(mod)}
          </TableCell>
        );
      });
    };

    const renderWorkbookModuleGrid = (senior) => {
      const userId = getSeniorUserId(senior);
      const rawModules = (userId && workbookModulesByUserId[userId]) || [];
      const hasLoaded = userId ? Object.prototype.hasOwnProperty.call(workbookModulesByUserId, userId) : false;
      const isLoading = userId ? !!workbookModulesLoadingByUserId[userId] : false;
      const maxModules = 6;

      const modulesByNumber = Array.from({ length: maxModules });
      if (Array.isArray(rawModules) && rawModules.length > 0) {
        for (let index = 0; index < rawModules.length; index++) {
          const mod = rawModules[index];
          const candidate = mod?.module_number ?? mod?.moduleNumber ?? null;
          const moduleNumber = Number(candidate);
          if (!Number.isFinite(moduleNumber)) continue;
          if (moduleNumber < 1 || moduleNumber > maxModules) continue;
          modulesByNumber[moduleNumber - 1] = chooseBetterModuleForNumber(mod, modulesByNumber[moduleNumber - 1]);
        }
      }

      const getStatus = (mod) => {
        if (!userId) return 'N/A';
        if (!hasLoaded || isLoading) return 'Loading';
        return getWorkbookCompletionStatus(mod);
      };

      return (
        <Grid container spacing={1} style={{ marginTop: 8 }}>
          {Array.from({ length: maxModules }, (_, index) => {
            const moduleNumber = index + 1;
            const mod = modulesByNumber[index];
            return (
              <Grid key={`${senior.id}-module-grid-${moduleNumber}`} item xs={4}>
                <Typography variant='caption' style={{ fontWeight: 600 }}>
                  Module {moduleNumber}
                </Typography>
                <Typography variant='body2'>{getStatus(mod)}</Typography>
              </Grid>
            );
          })}
        </Grid>
      );
    };

    const handleCardSortChange = (event) => {
      setSort(event.target.value);
    };

    const uniqueSeniors = React.useMemo(() => createUniqueList(seniors).filter(Boolean), [seniors]);
    const totalRowCount = uniqueSeniors.length;
    const emptyRows = store.perPage - Math.min(store.perPage, totalRowCount - store.page * store.perPage);

    if (auth.roles && auth.roles.length > 0 && !isFHT(auth.roles)) {
      return <PageUnauthorized />;
    }

    return (
      <div className={classes.frame}>
        {auth.roles && auth.roles.length > 0 && isFHT(auth.roles) && (
          <Grid container className={classes.whiteBg}>
            {auth.roles && !isStudent(auth.roles) && !isAlumni(auth.roles) && (
              <React.Fragment>
                <PleaseWait isLoading={store.loading} />
                <TitleBar title={pageTitle} currentPage='Student Workbooks' paths={[{ path: '/', name: 'Home' }]} additionalInformation='' />
                <div className={classes.frame}>
                  <div className={classes.alignPage}>
                    <Grid container className={classes.formatTableSize} align='left'>
                      <Grid item xs={12} sm={7} md={4} lg={3} className={classes.cardContainer} align='left'>
                        <Card className={classes.card}>
                          <CardBody className={classes.completedCardContent}>
                            <summary>
                              <div className={classes.completed}>Active Students</div>
                              <Tooltip
                                title={store.totalUniqueSeniors ? `Active Seniors: ${store.activeSeniors}/${store.totalUniqueSeniors}` : ''}
                                placement='top'
                              >
                                <div className={classes.totalSummary}>{store.activeSeniors}</div>
                              </Tooltip>
                            </summary>
                          </CardBody>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={7} md={4} lg={3} className={classes.cardContainer} align='left'>
                        <Card className={classes.card}>
                          <CardBody className={classes.completedCardContent}>
                            <summary>
                              <div className={classes.completed}>Modules 1-3 Completion %</div>
                              <Tooltip
                                title={
                                  store.total123Modules ? `Modules 1-3 Completed: ${store.total123ModulesCompleted}/${store.total123Modules}` : ''
                                }
                                placement='top'
                              >
                                <div className={classes.totalSummary}>
                                  {store.total123Modules ? `${Math.round((store.total123ModulesCompleted / store.total123Modules) * 100)}%` : '0%'}
                                </div>
                              </Tooltip>
                            </summary>
                          </CardBody>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={7} md={4} lg={3} className={classes.cardContainer} align='left'>
                        <Card className={classes.card}>
                          <CardBody className={classes.completedCardContent}>
                            <summary>
                              <div className={classes.completed}>Modules 4-7 Completion %</div>
                              <Tooltip
                                title={
                                  store.total4567Modules ? `Modules 4-7 Completed: ${store.total4567ModulesCompleted}/${store.total4567Modules}` : ''
                                }
                                placement='top'
                              >
                                <div className={classes.totalSummary}>
                                  {store.total4567Modules ? `${Math.round((store.total4567ModulesCompleted / store.total4567Modules) * 100)}%` : '0%'}
                                </div>
                              </Tooltip>
                            </summary>
                          </CardBody>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={7} md={4} lg={3} align='center' style={{ marginTop: '-30px' }}></Grid>
                    </Grid>
                  </div>

                  <div className={classes.alignPage}>
                    <Grid container className={classes.formatTableSize}>
                      <Grid item xs={12} className={classes.whiteBg} align='left'>
                        <Grid container direction='row'>
                          <Grid item xs={12} sm={12} md={6} lg={5} className={classes.whiteBg} align='left'>
                            <TextField
                              id='filter-field'
                              type='search'
                              className={classes.filter}
                              size='small'
                              hiddenLabel
                              placeholder='Filter by Name'
                              variant='outlined'
                              onChange={(event) => filterData(event.target.value)}
                              clearable='true'
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position='end'>
                                    <IconButton
                                      edge='end'
                                      color='primary'
                                      variant='contained'
                                      title='Clear Enhanced Filter'
                                      aria-describedby='Clear Filter Options'
                                      onClick={() => filterData('')}
                                    >
                                      <CancelPresentationIcon />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Hidden mdDown implementation='css'>
                          <TableContainer component={Paper}>
                            <Table className={tableClasses.table}>
                              <TableRow style={{ width: '100%' }}>
                                <Tooltip title='Click to sort by Last Name.' placement='top'>
                                  <TableCell onClick={() => setSort('last_name')} className={classes.sortHeader} style={{ width: '240px' }}>
                                    NAME (NDB ID){' '}
                                    <span
                                      className='fas fa-caret-up'
                                      style={{
                                        display: sortBy === 'last_name' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                      }}
                                    />
                                    <span
                                      className='fas fa-caret-down'
                                      style={{
                                        display: sortBy === 'last_name' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                      }}
                                    />
                                  </TableCell>
                                </Tooltip>
                                <TableCell className={classes.noSortHeader} style={{ width: '130px' }}>
                                  PLANNED GRADUATION DATE
                                </TableCell>
                                <TableCell className={classes.noSortHeader} style={{ width: '110px', textAlign: 'center' }}>
                                  MODULE 1
                                </TableCell>
                                <TableCell className={classes.noSortHeader} style={{ width: '110px', textAlign: 'center' }}>
                                  MODULE 2
                                </TableCell>
                                <TableCell className={classes.noSortHeader} style={{ width: '110px', textAlign: 'center' }}>
                                  MODULE 3
                                </TableCell>
                                <TableCell className={classes.noSortHeader} style={{ width: '110px', textAlign: 'center' }}>
                                  MODULE 4
                                </TableCell>
                                <TableCell className={classes.noSortHeader} style={{ width: '110px', textAlign: 'center' }}>
                                  MODULE 5
                                </TableCell>
                                <TableCell className={classes.noSortHeader} style={{ width: '110px', textAlign: 'center' }}>
                                  MODULE 6
                                </TableCell>
                              </TableRow>
                              <TableBody>
                                {store.searching ? (
                                  <TableRow style={{ width: '100%' }}>
                                    <TableCell align='center' colSpan={8}>
                                      <Typography variant='h5'>Searching...</Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  uniqueSeniors.slice(store.page * store.perPage, store.page * store.perPage + store.perPage).map((senior, idx) => (
                                    <TableRow
                                      style={{ width: '100%', height: '40px' }}
                                      key={senior.id + '_' + idx}
                                      className={classes.pointer}
                                      onClick={(ev) => handleClickId(ev, senior.id)}
                                    >
                                      <TableCell
                                        style={{
                                          width: '275px',
                                          marginTop: '4px',
                                          display: 'flex',
                                          justifyContent: 'flex-start',
                                          alignItems: 'flex-start',
                                        }}
                                      >
                                        {senior.first_name} {senior.last_name}{' '}
                                        {!getSeniorUserId(senior) && (
                                          <WarningIcon style={{ marginLeft: '2px', marginTop: '-2px', color: btOrangeColor }} />
                                        )}
                                      </TableCell>
                                      <TableCell style={{ width: '130px' }}>
                                        {senior.graduation_date > EPOCH_DATE && dateToMMMMYYYY(senior.graduation_date)}
                                      </TableCell>
                                      {renderWorkbookModuleCells(senior)}
                                    </TableRow>
                                  ))
                                )}

                                {!store.searching && totalRowCount === 0 ? (
                                  <TableRow>
                                    <TableCell align='center' colSpan={8} className={classes.noLeftPadding}>
                                      <Typography component='div' className={classes.noSeniorsMsg}>
                                        No records found.
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  <React.Fragment />
                                )}

                                {emptyRows > 0 && (
                                  <TableRow style={{ height: 34 * emptyRows }}>
                                    <TableCell colSpan={8} />
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>

                            <Typography variant='body2' className={classes.enrollmentWarning}>
                              <WarningIcon style={{ marginLeft: '2px', color: btOrangeColor }} /> = Student is not enrolled in Successful Futures.
                            </Typography>

                            <TablePagination
                              component='div'
                              colSpan={8}
                              SelectProps={{
                                InputProps: { 'aria-label': 'rows per page' },
                                MenuProps: { classes: { paper: classes.selectDropdown } },
                              }}
                              classes={{ menuItem: classes.menuItem }}
                              rowsPerPageOptions={[10, 15, 25, 50, 100, 150, 200, 250]}
                              count={totalRowCount}
                              rowsPerPage={store.perPage}
                              page={store.page}
                              onPageChange={(ev, val) => store.handlePageChange(ev, val)}
                              onRowsPerPageChange={(ev) => store.handleChangeRowsPerPage(ev)}
                            />
                          </TableContainer>
                        </Hidden>

                        <Hidden lgUp implementation='css'>
                          <Grid container>
                            <Grid item xs={12} sm={7} md={5} className={classes.radioRow}>
                              <RadioGroup
                                aria-label='card-sorting'
                                name='date-group'
                                className={classes.centerRadioSorting}
                                row
                                onChange={handleCardSortChange}
                              >
                                <FormControlLabel
                                  checked={sortBy === 'int_id'}
                                  control={<Radio color='default' />}
                                  label={<span className={classes.radioLabel}>Sort by Id</span>}
                                  onClick={() => setSort('int_id')}
                                  className={classes.radioLabel}
                                />
                                <aside
                                  style={{
                                    marginLeft: '-15px',
                                    marginTop: '8px',
                                    marginRight: '10px',
                                  }}
                                >
                                  <span
                                    className='fas fa-caret-up'
                                    style={{
                                      display: sortBy === 'int_id' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                  <span
                                    className='fas fa-caret-down'
                                    style={{
                                      display: sortBy === 'int_id' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                </aside>
                                <FormControlLabel
                                  checked={sortBy === 'last_name'}
                                  control={<Radio color='default' />}
                                  label={<span className={classes.radioLabel}>Sort by Last Name</span>}
                                  onClick={() => setSort('last_name')}
                                  className={classes.radioLabel}
                                  style={{
                                    marginLeft: '20px',
                                  }}
                                />
                                <aside
                                  style={{
                                    marginLeft: '-15px',
                                    marginTop: '8px',
                                    marginRight: '10px',
                                  }}
                                >
                                  <span
                                    className='fas fa-caret-up'
                                    style={{
                                      display: sortBy === 'last_name' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                  <span
                                    className='fas fa-caret-down'
                                    style={{
                                      display: sortBy === 'last_name' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                </aside>
                              </RadioGroup>
                            </Grid>
                          </Grid>

                          {seniors && seniors.length > 0 && (
                            <FamilyHomeListCards
                              SeniorStore={store}
                              seniors={seniors}
                              createUniqueList={createUniqueList}
                              renderWorkbookModuleCells={renderWorkbookModuleCells}
                              renderWorkbookModuleGrid={renderWorkbookModuleGrid}
                            />
                          )}
                        </Hidden>
                      </Grid>
                    </Grid>
                  </div>
                </div>
              </React.Fragment>
            )}
          </Grid>
        )}
      </div>
    );
  })
);

export default FamilyHomeListTable;
